import React from 'react';
import Section from '../components/ui/Section';
import ServiceCard from '../components/services/ServiceCard';
import CallToAction from '../components/home/CallToAction';
import { services } from '../data/services';
import { Bot, Puzzle, Flag, Shield, Target } from 'lucide-react';

const ServicesPage: React.FC = () => {
  const pitfalls = [
    {
      icon: <Bot size={24} className="text-primary" />,
      title: "Stopping at chatbots and copilots",
      description: "Many companies start with isolated pilots, like chatbots or internal copilots, but fail to move beyond them. This leaves the deeper potential of AI untapped, while competitors race ahead."
    },
    {
      icon: <Puzzle size={24} className="text-primary" />,
      title: "Fragmented execution",
      description: "In the absence of alignment, teams work in silos, leading to duplicated efforts, inflated costs, and increased risk."
    },
    {
      icon: <Flag size={24} className="text-primary" />,
      title: "No clear north star",
      description: "Without a defined vision for how AI creates value and how success will be measured, efforts become reactive and disconnected."
    },
    {
      icon: <Shield size={24} className="text-primary" />,
      title: "Neglecting Governance and Ethics",
      description: "Failing to put guardrails in place for responsible AI use can lead to reputational damage, legal exposure, and loss of trust."
    }
  ];

  const capabilities = [
    "Unlock core value for your business with AI",
    "Define your platform approach: build, buy, or partner",
    "Align governance, data, and risk to support scale",
    "Upskill your existing workforce",
    "Decades of experience driving AI innovation at global tech companies",
    "Proven track record leading transformative initiatives in Responsible AI, Generative AI, and large-scale product launches"
  ];

  return (
    <>
      <Section className="pt-32 md:pt-40">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Our <span className="text-primary">Services</span>
          </h1>
          <p className="text-xl md:text-2xl text-neutral-300">
            We provide end-to-end guidance for your AI journey, from strategy to implementation.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </div>
      </Section>
      
      <Section className="bg-neutral-900/50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">The AI Imperative</h2>
          <p className="text-lg text-neutral-300 mb-12 text-justify">
            The rise of Generative AI has triggered an industry-wide shift, and the pace isn't slowing down.
            Companies are under increasing pressure to act, driven by investor expectations and fear of being left
            behind. But while many are experimenting with AI, few have a clear strategy for turning those
            experiments into meaningful, scalable value. Even organizations with experience in AI are struggling to
            adapt to this rapidly evolving landscape. Those without established knowledge, infrastructure, or
            strategy face even greater challenges.
          </p>

          <h2 className="text-3xl md:text-4xl font-bold mb-6">Common Pitfalls</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
            {pitfalls.map((pitfall, index) => (
              <div key={index} className="card p-6">
                <div className="flex items-start mb-4">
                  <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center mr-4">
                    {pitfall.icon}
                  </div>
                  <h3 className="text-xl font-semibold">{pitfall.title}</h3>
                </div>
                <p className="text-neutral-300 text-justify">{pitfall.description}</p>
              </div>
            ))}
          </div>

          <h2 className="text-3xl md:text-4xl font-bold mb-6">Product Leap AI: Your Strategic Partner</h2>
          <div className="space-y-4">
            {capabilities.map((capability, index) => (
              <div key={index} className="flex items-center space-x-3 bg-neutral-800/50 p-4 rounded-lg">
                <Target size={20} className="text-primary flex-shrink-0" />
                <p className="text-neutral-300 text-justify">{capability}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>
      
      <CallToAction />
    </>
  );
};

export default ServicesPage;